import { ActionIconProps, Tooltip } from "@mantine/core";
import { ActionIcon, Box } from "@mantine/core";
import { FaRegStar, FaStar } from "react-icons/fa";
import styles from "./StarToggle.module.css";

export type StarSize = "sm" | "md" | "lg" | "xl" | "2xl";

interface StarToggleProps extends ActionIconProps {
  /**
   * Whether the star is selected (filled)
   */
  isSelected: boolean;

  /**
   * Callback when star is clicked
   */
  onToggle: () => void;

  /**
   * Size of the star icon
   * - sm: 18px (list view)
   * - md: 20px (modal)
   * - lg: 24px (detail hero)
   */
  size?: StarSize;

  /**
   * Color of the star when selected
   */
  color?: string;

  /**
   * Whether the toggle is disabled
   */
  disabled?: boolean;

  /**
   * Custom aria-label for accessibility
   */
  ariaLabel?: string;

  /**
   * Custom styles for the button
   */
  style?: React.CSSProperties;

  /**
   * Custom class name
   */
  className?: string;
  withGlow?: boolean;

  /**
   * Topic id for E2E (data-topic-id, data-testid)
   */
  topicId?: string;
}

const sizeMap: Record<StarSize, number> = {
  sm: 22,
  md: 24,
  lg: 28,
  xl: 36,
  "2xl": 40,
};

function StarToggle({
  isSelected,
  onToggle,
  size = "md",
  color = "currentColor",
  disabled = false,
  ariaLabel,
  style,
  className,
  withGlow = false,
  topicId,
  ...rest
}: StarToggleProps) {
  const iconSize = sizeMap[size];
  const StarIcon = isSelected ? FaStar : FaRegStar;

  const tooltipContent = isSelected ? "Remove from priorities" : "Mark for discussion";

  const label = ariaLabel || (isSelected ? "Remove priority" : "Mark topic as priority");

  return (
    <Tooltip label={tooltipContent} position="top" withArrow>
      <ActionIcon
        aria-label={label}
        variant="transparent"
        size="xl" // Ensure click target is large enough
        data-testid="priority-star-toggle"
        data-topic-id={topicId}
        data-selected={isSelected ? "true" : "false"}
        {...rest}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        disabled={disabled}
        color={isSelected ? color : "white"}
        className={`${className} ${withGlow ? styles.withGlow : ""}`}
        style={{
          // 🔒 EXCEPTION: Dynamic color from props
          color: isSelected ? color : "white",
          ...style,
        }}
      >
        <Box
          component={StarIcon}
          className={`${styles.starIcon} ${isSelected && withGlow ? styles.selected : ""}`}
          style={{
            width: iconSize,
            height: iconSize,
          }}
        />
      </ActionIcon>
    </Tooltip>
  );
}

export type { StarToggleProps };

export default StarToggle;
